import {DocumentNode} from '@apollo/client/core';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {fadeIn, fadeSmooth, ROUTE_ANIMATIONS_ELEMENTS} from '../../animations/router-animation';
import {FormControl, FormGroup, UntypedFormControl} from '@angular/forms';
import {ActionButton} from './action-button.model';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {merge, Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, first, map, mapTo, startWith, switchMap} from 'rxjs/operators';
import {PageFilteringInput} from './data-page.model';

import {MustHaveFilter} from './must-have-filters.model';
import {SelectionModel} from '@angular/cdk/collections';
import {TableColumn, TableConfiguration, UserTypesControl} from './paginated-table-configuration.model';
import {NotificationService} from "../../../services/notification.service";
import {GraphqlService} from "../../../services/graphql.service";
import {SettingsService} from "../../../services/settings.service";
import {BaseResponse} from "../../../common/interfaces/global-interfaces";
import {Action} from "@ngrx/store";
import {Actions, ofType} from "@ngrx/effects";
import {FieldsService} from "../../../services/fields.service";
import { StorageService } from '../../../services/storage.service';
import { UserTypeEnum } from '../../../store/entities/uaa/auth-user.model';
import _ from "lodash";

@Component({
  selector: 'app-paginated-data-table',
  templateUrl: './paginated-data-table.component.html',
  styleUrls: ['./paginated-data-table.component.scss'],
  animations: [fadeSmooth, fadeIn]
})
export class PaginatedDataTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {


  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  /** Table Inputs */
  @Input() tableList = [];
  @Input() query: any;
  /** Trigger Server Request */
  @Input() resetTable = false;
  @Input() exportGraph?: { baseUrl?: string, query?: DocumentNode, variables: any, queryKey: any, fileName?: string };
  @Input() filters?: MustHaveFilter[];
  @Input() permissions: any = {};
  @Input() userTypes: UserTypesControl = {};
  @Input() noSearchFields: string[] = [];
  @Input() customSearchFields: string[] = [];
  @Input() initialDataSelected: any[] = [];
  @Input() initialSortField?: { key: string, sortOrder: 'ASC' | 'DESC' };
  @Input() additionalVariables?: { [id: string]: any };
  @Input() tableConfigurations: TableConfiguration = {
    tableColumns: [],
    tableCaption: '',
    allowPagination: true,
    tableNotifications: null,
    useRowClick: true,
    actionIcons: {
      edit: false,
      delete: false,
      more: false,
      customPrimary: false,
      checkBox: false
    },
    doneLoading: false,
    deleting: {},
    active: {},
    error: {},
    loading: {},
    showSearch: true,
    showBorder: true,
    showNumbers: false,
    empty_msg: 'No Data',
    customPrimaryMessage: '',
    useFullObject: false,
  };
  @Input() loading: boolean = false;
  @Input() showExportButton: boolean = false;
  tableLoading: boolean = false;
  searchFieldControl: UntypedFormControl;

  /** Table Events */
  @Output() rowCheckBox: EventEmitter<any> = new EventEmitter();
  @Output() rowCustomPrimary: EventEmitter<any> = new EventEmitter();
  @Output() rowUpdate: EventEmitter<any> = new EventEmitter();
  @Output() rowDownload: EventEmitter<any> = new EventEmitter();
  @Output() rowDelete: EventEmitter<any> = new EventEmitter();
  @Output() rowPreview: EventEmitter<any> = new EventEmitter();
  @Output() rowPrint: EventEmitter<any> = new EventEmitter();
  @Output() totalRecords: EventEmitter<any> = new EventEmitter();

  @Output() customAction: EventEmitter<any> = new EventEmitter();

  @Input() loadingMessage?: string;

  // list of all the added buttons that you want to deal with separately
  @Input() actionButtons: ActionButton[] = [];
  @Output() getData = new EventEmitter();

  @Input() upsertAction: Action = undefined;
  @Input() deleteAction: Action = undefined;
  @Output() onLoadData = new EventEmitter();
  actions$ = inject(Actions);

  subscriptions: Subscription = new Subscription();
  /** list of fields that are searchable */
  showDelete: any = {};
  searchFields: string;
  searchQuery: string = '';
  showDownload: any = {};
  doSearch = false;
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  selection = new SelectionModel<any>(true, []);
  displayedColumns: string[] = [];
  showButtonConfirm: any = {};
  @ViewChild(MatPaginator, {static: true}) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;
  @ViewChild('input') input?: ElementRef;
  @Input() pageSize = 10;
  @Input() pageSizeOptions = [5, 10, 20, 50, 100];
  resultLength?: number;
  loadingExportData: boolean = false;
  @Input() queryKey = 'items';
  @Input() fetchPolicy: 'cache-first' | 'network-only' = 'network-only';
  tempData?: BaseResponse<any[]>;
  // @Input() demoData: DataPage = null
  selectedItemsTracker: any[] = [];
  selectedCheckboxTracker: { [key: string]: any } = {};
  // TODO: To be removed
  selectedCheckboxTrackerItems = {};
  private resetDataTable$: Subject<boolean> = new Subject<boolean>();
  @Input() showDateRange: boolean = false;
  readonly rangeForm = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  userType: UserTypeEnum;

  constructor(
    private graphService: GraphqlService,
    private settings: SettingsService,
    private notificationService: NotificationService,
    private fs: FieldsService,
    private storageService: StorageService,
  ) {
    this.userType = this.storageService.get('userType');
    this.searchFieldControl = new UntypedFormControl('');
    this.tableConfigurations.showSearch =
      this.tableConfigurations.showSearch !== null
        ? this.tableConfigurations.showSearch
        : true;
    this.tableConfigurations.allowPagination =
      this.tableConfigurations.allowPagination !== null
        ? this.tableConfigurations.allowPagination
        : true;
    this.tableConfigurations.showBorder =
      this.tableConfigurations.showBorder !== null
        ? this.tableConfigurations.showBorder
        : false;
    this.searchFields = this.tableConfigurations.tableColumns
      .map(column => column.name)
      .join(',');
    this.tableConfigurations.actionIcons = this.tableConfigurations
      .actionIcons
      ? this.tableConfigurations.actionIcons
      : {
        edit: false,
        delete: false,
        download: false,
        more: false,
        print: false,
        cancel: false,
        customPrimary: false,
        checkBox: false,
      };
    this.tableConfigurations.active = this.tableConfigurations.active
      ? this.tableConfigurations.active
      : {};

  }

  @Input() mapFunction: (item: any) => {} = (item: any) => {
    return {
      ...item,
    };
  }

  ngOnChanges(): void {
    if (this.resetTable || this.filters != null) {
      this.resetDataTable$.next(true);
      this.showDelete = {};
      this.resetTable = false
    } else {
      this.resetDataTable$.next(false);
    }
    if (this.tableConfigurations.actionIcons.checkBox) {
    }
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator ?? null;
    this.dataSource.sort = this.sort ?? null;

    this.prepareTableColumns();


    if (this.tableList?.length) this.dataSource.data = this.tableList; // Dummy data

    if (this.upsertAction) {
      this.subscriptions.add(
        this.actions$.pipe(ofType(<any>this.upsertAction)).subscribe(data => this.upsertData(data))
      );
    }

    if (this.deleteAction) {
      this.subscriptions.add(
        this.actions$.pipe(ofType(<any>this.deleteAction)).subscribe(data => this.removeData(data))
      );
    }

    this.dateChanged();
  }


  upsertData(event) {
    let data;
    [0, 1].forEach(i => !data && (data = this.fs.fieldsArrayFromObj(event)?.[i].value)?.id);
    if (!data) return;

    if (Array.isArray(data)) {
      data.forEach(x => {
        let items = this.dataSource?.data?.filter(item => item.id !== x.id).concat([x]);
        items = this.mapper(items)
        this.getData.emit(items);
        this.dataSource.data = items;
      });
    } else {
      let items = this.dataSource?.data?.filter(item => item.id !== data.id).concat([data]);
      items = this.mapper(items)
      this.dataSource.data = items;
      this.getData.emit(items);
    }
  }

  mapper(items: any) {
    return this.mapFunction ? items?.map(y => this.mapFunction(y)) : items;
  }

  removeData(event) {
    this.dataSource.data = this.dataSource?.data?.filter(item => item?.id.toString() !== event?.id);
    this.getData.emit(this.dataSource?.data);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  search() {
    setTimeout(async () => {
      // const fields = this.prepareFields();
      // this.tableLoading = true;
      let index: number | undefined;
      // if (this.paginator) index = this.paginator.pageIndex + 1;
      if (this.paginator) index = 1;
      this.paginator.pageIndex = 1;
      const data = await this.getDataFromSource(index, this.paginator?.pageSize, this.searchQuery).pipe(first()).toPromise();
      this.prepareDataAndColumns(data);
      // this.prepareDataAndColumns(this.demoData);
      // this.tableLoading = false;
    }, 100);
  }


  _handleKeydown(event: KeyboardEvent) {

    // Prevent propagation for all alphanumeric characters in order to avoid selection issues
    if ((event.key !== undefined) && ((event.code === 'Space') || ((event.code === 'Home' || event.code === 'end')))) {
      event.stopPropagation();
    }

    if (event.key && event.code === 'Enter') {
      this.doSearch = true;
      this.search();

    }

    if (event.code === 'Escape' && this.searchQuery) {
      this.searchQuery = '';
      event.stopPropagation();
    }

  }

  _handleKeyup(event: KeyboardEvent) {
    if (event.code === 'Backspace' && this.searchQuery.length === 0) {
      if (this.tempData?.data?.length && this.tempData?.data?.length > 0) {
        this.prepareDataAndColumns(this.tempData);
      } else {
        this.search();
      }
    }
  }


  ngAfterViewInit() {
    setTimeout(() => {
      merge(this.sort ? this.sort.sortChange : null, this.paginator?.page ?? null, this.resetDataTable$.pipe(mapTo(true)))
        .pipe(
          startWith({}),
          // tap(i => this.tableLoading = true),
          switchMap((i: any) => {
            /*  let fields: FieldRequestInput[] = [];
              if ((this.system-configurations.isObject(i) && i.hasOwnProperty('active') || this.initialSortField)) {
                fields = this.prepareFields();
              }*/
            let index: number | undefined;
            if (this.paginator) index = this.paginator.pageIndex + 1;
            return this.getDataFromSource(index, this.paginator?.pageSize, this.searchQuery);
          })
        )
        .subscribe(
          (i: any) => this.prepareDataAndColumns(i)
        );
    }, 10);
  }

  prepareTableColumns() {
    const {edit, print, more, cancel, download, customPrimary, checkBox} = this.tableConfigurations.actionIcons;
    let columns: string[];
    if (this.tableConfigurations.showNumbers) {
      columns = [
        'position',
        ...this.tableConfigurations.tableColumns.map(column => column.name)
      ];
    } else {
      columns = [
        ...this.tableConfigurations.tableColumns.map(column => column.name)
      ];
    }
    if (
      edit ||
      print ||
      download ||
      more ||
      cancel ||
      customPrimary ||
      checkBox ||
      this.tableConfigurations.actionIcons.delete ||
      this.actionButtons.length > 0
    ) {
      this.displayedColumns = [...columns, 'action'];
    } else {
      this.displayedColumns = columns;
    }
    if (this.tableConfigurations.allowPagination && this.dataSource.paginator) {
      this.dataSource.paginator = this.paginator ?? null;
    }
    this.dataSource.sort = this.sort ?? null;
  }

  prepareDataAndColumns(response: BaseResponse<any[]>) {
    this.tableLoading = false;
    if (!this.doSearch) {
      this.tempData = response;
    } else {
      this.doSearch = false;
    }
    const dataValues = response && response.data ? response.data : [];
    const mappedValues = this.mapFunction ? dataValues.map(i => this.mapFunction(i)) : dataValues;
    this.dataSource = new MatTableDataSource(mappedValues);
    if (this.selection.hasValue()) {
      const tableSelection = this.settings.removeDuplicates(this.selection.selected, 'id').map(item => item['id']);
      this.initialDataSelected = (this.initialDataSelected || []).concat(tableSelection.filter(item => (this.initialDataSelected || []).indexOf(item['id']) === -1));
    }
    this.dataSource.data.forEach(row => {
      if (this.initialDataSelected?.some(item => item + '' === row['id'] + '')) {
        this.selection.select(row);
      }
    });

    this.totalRecords.emit(response?.page?.totalElements);
    this.getData.emit(mappedValues);
    // this.resultLength = this.searchQuery ? response?.recordsFilteredCount || response?.page?.totalElements : response?.page?.totalElements;
    this.resultLength = response?.page?.totalElements;
    this.loading = false;
    this.prepareTableColumns();
  }

  /*  prepareFields(): FieldRequestInput[] {
      const fields: FieldRequestInput[] = [];
      this.tableConfigurations.tableColumns.forEach(column => {
        let value: any = {
          fieldName: column.name,
          isSearchable: true,
          operation: 'LK',
          searchValue: this.input?.nativeElement.value
        };
        if (this.initialSortField) {
          if (this.initialSortField.key === column.name) {
            value = {
              ...value,
              isSortable: true,
              orderDirection: this.initialSortField.sortOrder
            };
          }
        }
        if (this.sort?.active && this.sort.direction) {
          value = {
            ...value,
            isSortable: true,
            orderDirection: this.sort.direction.toUpperCase()
          };
        } else {
          if (this.initialSortField && this.initialSortField.key !== column.name) {
            if (value.hasOwnProperty('isSortable')) {
              delete value.isSortable;
            }
            if (value.hasOwnProperty('orderDirection')) {
              delete value.orderDirection;
            }
          }
        }
        if (this.noSearchFields.indexOf(value.fieldName) === -1) {
          fields.push(value);
        }
      });

      this.customSearchFields.map(d => {
        const customFields: FieldRequestInput = {
          fieldName: d,
          isSearchable: true,
          operation: 'LK',
          searchValue: this.input?.nativeElement.value,
        };
        fields.push(customFields);
      });

      return fields;
    }*/

  // Method to get data
  getDataFromSource(pageNumber?: number, itemsPerPage?: number, searchKeyword?: string): Observable<BaseResponse<any[]>> {
    const hasKeyWord = !!searchKeyword;
    try {
      this.tableLoading = true;
      let filtering: PageFilteringInput = {
        pageNumber: pageNumber ?? 0,
        itemsPerPage: itemsPerPage ?? 0,
       ...(hasKeyWord && searchKeyword ? { keyword: searchKeyword } : {}),
        ...this.additionalVariables
      };
      let variables = {filtering};
      variables.filtering.pageNumber = pageNumber ?? variables.filtering.pageNumber;
      variables.filtering.itemsPerPage = itemsPerPage ?? variables.filtering.itemsPerPage;
      if (!this.query) return;
      return this.graphService.queryObservable(
        {
          query: _.cloneDeep(this.query),
          variables
        },
        this.fetchPolicy)
        .pipe(map(({data, errors}: any) => {
          if (errors) {
            console.error(errors);
            this.notificationService.errorMessage(errors[0]?.message);
          }
          return data ? Object.values(data)[0] : errors
        }), catchError(error => {
          this.tableLoading = false;
          this.notificationService.errorMessage(error.message || error[0].message);
          return of([]);
        }));
    } catch (error: any) {
      console.error(error);
      this.tableLoading = false;
      this.notificationService.errorMessage(error.message || error[0].message);
      return of();
    }
  }

  viewItemDetails(item: any) {
    if (this.tableConfigurations.useFullObject) {
      this.rowPreview.emit(item);
    } else {
      this.rowPreview.emit(item.id);
    }
  }

  editItem(item: any) {
    if (this.tableConfigurations.useFullObject) {
      this.rowUpdate.emit(item);
    } else {
      this.rowUpdate.emit(item.id);
    }
  }

  printItem(itemId: any) {
    this.rowPrint.emit(itemId);
  }

  deleteItem(item: any) {
    if (this.tableConfigurations.useFullObject) {
      this.rowDelete.emit(item);
    } else {
      this.rowDelete.emit(item.id);
    }
  }

  customPrimaryItem(item: any) {
    if (this.tableConfigurations.useFullObject) {
      this.rowCustomPrimary.emit(item);
    } else {
      this.rowCustomPrimary.emit(item.id);
    }
  }

  rowCheckBoxItem($event) {
    if (this.tableConfigurations.useFullObject) {
      if ($event && !Array.isArray($event)) {
        const itemIndex = this.selectedItemsTracker.findIndex(d => d.id + '' === $event.id + '');
        this.selectedCheckboxTracker[$event.id] = $event.isChecked;
        this.selectedCheckboxTrackerItems[$event.id] = $event;
        if (itemIndex === -1) {
          this.selectedItemsTracker.push($event);
        } else {
          this.selectedItemsTracker[itemIndex] = $event;
        }
      } else if (Array.isArray($event)) {
        $event.forEach(d => {
          d.isChecked = this.selectedCheckboxTracker[d.id];
          const itemIndex = this.selectedItemsTracker.findIndex(f => d.id + '' === f.id + '');
          if (itemIndex === -1) {
            this.selectedItemsTracker.push(d);
          } else {
            this.selectedItemsTracker[itemIndex] = d;
          }
        });
      }
      let selectedItems = this.settings.removeDuplicates(this.selectedItemsTracker.filter(j => j.isChecked), 'id');
      this.initialDataSelected = selectedItems.filter(j => j.isChecked).map(j => j.id);
      this.rowCheckBox.emit(selectedItems);
    } else {
      this.rowCheckBox.emit(this.initialDataSelected);
    }
  }

  downloadItem(item: any) {
    this.rowDownload.emit(item);
  }

  trackByFn(index: any, item: any) {
    return item ? item.id : undefined;
  }

  submitCustomButtom(button: ActionButton, item: any, step: string) {
    const action = button.action;
    this.showButtonConfirm = {};
    if (step === 'first' && button.confirm_first) {
      this.showButtonConfirm[button.id + item.id] = true;
    } else {
      if (this.tableConfigurations.useFullObject) {
        this.customAction.emit({action, value: item});
      } else {
        this.customAction.emit({action, value: item.id});
      }
    }
  }

  async downloadToCsv() {

  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    this.selection.selected.forEach(f => {
      f.isChecked = true;
      this.selectedCheckboxTracker[f.id] = f.isChecked;
      this.selectedCheckboxTrackerItems[f.id] = f;
    })
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  onSelectAll($event) {
    console.log($event);
    if ($event.checked) {
      this.selection.clear();
      this.dataSource.data.forEach(row => {
        row.isChecked = true;
        this.selectedCheckboxTracker[row.id] = row.isChecked;
        this.rowCheckBoxItem(row)
        return this.selection.select(row);
      });
    } else {
      this.selection.clear();
      this.dataSource.data.forEach(row => {
        row.isChecked = false;
        this.selectedCheckboxTracker[row.id] = row.isChecked;
        this.rowCheckBoxItem(row)
        return this.selection.deselect(row);
      });
    }
  }

  onCheckboxClick($event, tableListItem) {
    if ($event.checked) {
      this.selection.select(tableListItem)
    } else {
      this.selection.deselect(tableListItem)
    }
    tableListItem.isChecked = this.selection.isSelected(tableListItem);
    // console.log(tableListItem.isChecked);
    this.rowCheckBoxItem(tableListItem)
  }

  onRowClick(tableListItem) {
    if (this.tableConfigurations?.useRowClick) {
      if (this.tableConfigurations?.actionIcons?.checkBox) {
        let isChecked = this.selection.isSelected(tableListItem);
        if (!isChecked) {
          this.selection.select(tableListItem)
        } else {
          this.selection.deselect(tableListItem)
        }
        tableListItem.isChecked = this.selection.isSelected(tableListItem);
        this.rowCheckBoxItem(tableListItem)
      }
    }

  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  // masterToggle() {
  //   this.isAllSelected() ?
  //     this.selection.clear() :
  //     this.dataSource.data.forEach(row => {
  //       row.isChecked = this.selection.isSelected(row);
  //       // this.selectedCheckboxTracker[row.id] = row.isChecked;
  //       return this.selection.select(row);
  //     });
  // }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    } else {
      row.isChecked = this.selection.isSelected(row);
      this.selectedCheckboxTracker[row.id] = row.isChecked;
      this.selectedCheckboxTrackerItems[row.id] = row;
      return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
    }
  }

  tdClick(column: TableColumn, value: any) {
    if (column.link) {
      console.log(column);
      console.log(value);
    }
  }

  rowCLicked() {
    // console.log('clicked')
  }

  onChangePage($event) {
    // console.log($event);
  }

  dateChanged () {
    this.subscriptions.add(
      this.rangeForm?.valueChanges?.subscribe(d => {
        if (d?.start && d?.end && d?.end >= d?.start) {
          console.log('rangeForm', d);
          this.additionalVariables = {
            ...this.additionalVariables,
            startDate: this.settings.formatDateStartWithYearAndDash(d?.start),
            endDate: this.settings.formatDateStartWithYearAndDash(d?.end)
          };
          this.resetDataTable$.next(true);
        }
      })
    );
  }

}
